/* WI Testimonial Stories — Webinterest (webinterest.in) */
if (!customElements.get('wi-testimonial-stories')) {
  customElements.define('wi-testimonial-stories', class extends HTMLElement {
    connectedCallback() {
      if (this.ac && !this.ac.signal.aborted) return;
      this.rail = this.querySelector('[data-rail]');
      this.modal = this.querySelector('[data-modal]');
      this.dialog = this.modal?.querySelector('.wi-ts__dialog');
      this.closeButton = this.modal?.querySelector('[data-close]');
      if (!this.rail || !this.modal || !this.dialog) return;

      this.ac = new AbortController();
      const o = { signal: this.ac.signal };
      this.triggers = [...this.rail.querySelectorAll('[data-trigger]')];
      this.slides = [...this.modal.querySelectorAll('[data-slide]')];
      this.videos = [...this.modal.querySelectorAll('video')];
      this.count = this.modal.querySelector('[data-count]');
      this.index = 0;

      this.initRail(o);
      this.initModal(o);

      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', (e) => {
          this.open(this.triggers.findIndex((t) => t.dataset.blockId === e.detail.blockId));
        }, o);
        this.addEventListener('shopify:block:deselect', () => this.modal.open && this.modal.close(), o);
      }
    }

    disconnectedCallback() {
      this.ac?.abort();
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      this.pauseAll();
      if (this.modal?.open) this.modal.close();
    }

    initRail(o) {
      const rail = this.rail;
      const reduce = matchMedia('(prefers-reduced-motion: reduce)');
      const max = () => rail.scrollWidth - rail.clientWidth;
      let target = 0;
      let drag = null;
      let moved = false;

      const step = () => {
        const d = target - rail.scrollLeft;
        if (Math.abs(d) < 1) { rail.scrollLeft = target; this.raf = 0; return; }
        rail.scrollLeft += Math.abs(d * 0.2) < 1 ? Math.sign(d) : d * 0.2;
        this.raf = requestAnimationFrame(step);
      };

      if (this.dataset.wheel === 'true') {
        rail.addEventListener('wheel', (e) => {
          if (e.ctrlKey || Math.abs(e.deltaX) >= Math.abs(e.deltaY)) return;
          const dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? rail.clientWidth : 1);
          const base = this.raf ? target : rail.scrollLeft;
          if ((dy < 0 && base <= 0) || (dy > 0 && base >= max() - 1)) return;
          e.preventDefault();
          target = Math.max(0, Math.min(base + dy, max()));
          if (reduce.matches) rail.scrollLeft = target;
          else if (!this.raf) this.raf = requestAnimationFrame(step);
        }, { ...o, passive: false });
      }

      rail.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        cancelAnimationFrame(this.raf);
        this.raf = 0;
        drag = { x: e.clientX, left: rail.scrollLeft, id: e.pointerId };
        moved = false;
      }, o);

      rail.addEventListener('pointermove', (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const dx = e.clientX - drag.x;
        if (!moved) {
          if (Math.abs(dx) < 6) return;
          moved = true;
          rail.classList.add('is-drag');
          rail.setPointerCapture(e.pointerId);
        }
        rail.scrollLeft = drag.left - dx;
      }, o);

      const end = () => { drag = null; rail.classList.remove('is-drag'); };
      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((t) => rail.addEventListener(t, end, o));
      rail.addEventListener('dragstart', (e) => e.preventDefault(), o);

      rail.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-trigger]');
        if (!trigger) return;
        if (moved) { moved = false; e.preventDefault(); return; }
        this.open(this.triggers.indexOf(trigger));
      }, o);
    }

    initModal(o) {
      const m = this.modal;
      let sx = 0;
      let sy = 0;
      let skipSwipe = false;

      m.addEventListener('click', (e) => {
        const t = e.target;
        if (t.closest('[data-close]')) return m.close();
        if (t.closest('[data-prev]')) return this.show(this.index - 1);
        if (t.closest('[data-next]')) return this.show(this.index + 1);
        const play = t.closest('[data-play]');
        if (play) return this.play(play.parentElement.querySelector('video'));

        if (t === m || t === this.dialog) m.close();
      }, o);

      m.addEventListener('close', () => {
        this.pauseAll();
        if (this.lastFocus?.isConnected) this.lastFocus.focus({ preventScroll: true });
      }, o);

      m.addEventListener('keydown', (e) => {
        if (this.slides.length < 2 || e.target.closest('video')) return;
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.show(this.index - 1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.show(this.index + 1);
        }
      }, o);

      m.addEventListener('touchstart', (e) => {
        const t = e.changedTouches[0];
        const interactive = e.target.closest('button, a, input, select, textarea, video');
        const v = e.target.closest('video');
        sx = t.clientX;
        sy = t.clientY;
        skipSwipe = Boolean(interactive) || (!!v && t.clientY > v.getBoundingClientRect().bottom - 64);
      }, { ...o, passive: true });

      m.addEventListener('touchend', (e) => {
        if (skipSwipe || this.slides.length < 2) {
          skipSwipe = false;
          return;
        }
        const t = e.changedTouches[0];
        const dx = t.clientX - sx;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(t.clientY - sy)) {
          this.show(this.index + (dx < 0 ? 1 : -1));
        }
        skipSwipe = false;
      }, { ...o, passive: true });

      this.videos.forEach((v) => {
        const btn = v.parentElement.querySelector('[data-play]');
        v.playsInline = true;
        v.setAttribute('playsinline', '');
        if (!btn) return;
        v.addEventListener('play', () => { btn.hidden = true; }, o);
        v.addEventListener('pause', () => { btn.hidden = false; }, o);
      });
    }

    open(i) {
      if (i < 0 || !this.slides[i]) return;
      this.lastFocus = document.activeElement;
      if (!this.modal.open) this.modal.showModal();
      this.show(i);
      requestAnimationFrame(() => this.closeButton?.focus({ preventScroll: true }));
    }

    show(i) {
      const n = this.slides.length;
      this.index = (i + n) % n;
      this.pauseAll(true);
      this.slides.forEach((s, k) => { s.hidden = k !== this.index; });
      if (this.count) this.count.textContent = `${this.index + 1} / ${n}`;
      if (this.dataset.autoplay === 'true') this.play(this.slides[this.index].querySelector('video'));
    }

    async play(v) {
      if (!v) return;
      try {
        await v.play();
      } catch {
        v.muted = true;
        v.play().catch(() => {});
      }
    }

    pauseAll(reset) {
      this.videos?.forEach((v) => {
        v.pause();
        if (reset && v.currentTime) v.currentTime = 0;
      });
    }
  });
}
